import React, { useEffect } from "react";

import Chip from "@mui/material/Chip";
import Stack from "@mui/material/Stack";

import i18next from "i18next";
import toast from "react-hot-toast";
import { useNavigate, useParams } from "react-router";

import BasicInput from "../../components/BasicInput";
import Button from "../../components/Button";
import ConfirmDialog from "../../components/ConfirmDialog";
import FieldTitle from "../../components/FieldTitle";
import Label from "../../components/Label";
import SimpleDialog, { DialogPosition } from "../../components/SimpleDialog";
import AddLocationButtonsGroup from "../AddLocationButtonsGroup";

import { useDispatch } from "react-redux";
import ErrorMessage from "../../components/ErrorMessage";
import * as extractionServices from "../../services/extractionsServices";
import { createLocation } from "../../services/locationsServices";
import { setSelectedLocation, setWatchLocation } from "../../state/map/mapSlice";
import { constants, routes, utils } from "../../utils";
import { SELECT_LOCATION_MODE } from "../../utils/constants";
import { Extraction as ExtraccionType } from "../../utils/types";
import SearchCoordinate from "../SearchCoordinate";

export default function Extraction() {
	const navigate = useNavigate();
	const { id } = useParams();

	const dispatch = useDispatch();

	const [errors, setErrors] = React.useState<any>({});
	const [extraction, setExtraction] = React.useState<any>({});
	const [location, setLocation] = React.useState<any>({});
	const [selectLocationMode, setSelectLocationMode] = React.useState<string>(
		constants.SELECT_LOCATION_MODE.enterLatitudeLongitude
	);
	const [selectingLocation, setSelectingLocation] = React.useState<boolean>(false);
	const [editing, setEditing] = React.useState<boolean>(false);
	const [deleteModal, setDeleteModal] = React.useState<boolean>(false);
	const [showValue, setShowValue] = React.useState<boolean>(false);

	useEffect(() => {
		if (id) {
			extractionServices.getExtraction(id).then((data) => {
				setExtraction(data);
				setLocation({ coordinates: data.coordinates, name: data.name });
			});
		}
	}, [id]);

	useEffect(() => {
		if (!selectingLocation) {
			setErrors({});
		}
	}, [selectingLocation]);

	useEffect(() => {
		const showValue = !!id && !editing;
		if (showValue && extraction.coordinates) {
			dispatch(setSelectedLocation({ lat: extraction.coordinates.lat, lng: extraction.coordinates.lng }));
			dispatch(setWatchLocation(true));
		}
		setShowValue(showValue);

		return () => {
			dispatch(setWatchLocation(false));
			dispatch(setSelectedLocation({ lat: 0, lng: 0 }));
		};
	}, [id, editing, extraction]);

	const handleDelete = async () => {
		try {
			await extractionServices.deleteExtraction(extraction.id);
			toast.success(i18next.t("extraction-delete-success"));
			navigate(routes.EXTRACTION_LIST, { replace: true });
		} catch (error: any) {
			setDeleteModal(false);
			toast.error(i18next.t(error?.response?.data?.detail ?? "error"));
		}
	};

	useEffect(() => {
		setExtraction({ ...extraction, ...location });
	}, [location]);

	const onClose = () => {
		navigate(-1);
	};

	const handleChangeExtraction = (extraction: ExtraccionType) => {
		setExtraction(extraction);
		if (Object.keys(errors).length !== 0) validate(extraction);
	};

	const validate = (extractionParam = extraction) => {
		const errors: any = {};

		if (!extractionParam.code) {
			errors.code = i18next.t("extraction-identifier-required");
		}

		if (!extractionParam.type_extraction) {
			errors.type_extraction = i18next.t("extraction-type-required");
		}

		if (
			isNaN(parseFloat(extractionParam.coordinates?.lat)) ||
			isNaN(parseFloat(extractionParam.coordinates?.lng))
		) {
			errors.location = i18next.t("location-required");
		}

		setErrors(errors);

		return Object.keys(errors).length === 0 ? {} : errors;
	};

	const goBack = () => navigate(routes.DASHBOARD);

	const handleCreateEditExtraction = async () => {
		try {
			const errors = validate();
			if (Object.keys(errors).length !== 0) {
				toast.error(i18next.t("fill-required"));
				return;
			}

			var response: any;
			if (id) {
				response = await extractionServices.updateExtraction(extraction);
			} else {
				if (selectLocationMode === SELECT_LOCATION_MODE.locationAdd) {
					try {
						const locationResponse = await createLocation(location);
						extraction.location_id = locationResponse.data.id;
					} catch (error) {
						toast.error(i18next.t("error-message"));
						return;
					}
				}
				response = await extractionServices.createExtraction(extraction);
			}

			toast.success(i18next.t(editing ? "extraction-edit-success" : "extraction-created-success"));
			goBack();
		} catch (error: any) {
			toast.error(i18next.t(error?.response?.data?.detail ?? "error"));
		}
	};

	if (selectingLocation) {
		return (
			<SearchCoordinate
				type={selectLocationMode}
				setSelectingLocation={setSelectingLocation}
				location={location}
				handleAccept={({ id, lat, lng, name }) => {
					setLocation({ id, name, coordinates: { lat, lng } });
					setSelectingLocation(false);
				}}
				handleCancel={() => setSelectingLocation(false)}
			/>
		);
	}

	return (
		<>
			<ConfirmDialog
				title={i18next.t("delete")}
				handleCancel={() => setDeleteModal(false)}
				handleConfirm={handleDelete}
				subtitle={i18next.t("extraction-delete-confirm")}
				open={deleteModal}
			/>

			<SimpleDialog
				open={true}
				onClose={onClose}
				position={showValue ? DialogPosition.Top : DialogPosition.Center}
			>
				<div className="d-flex justify-content-between">
					<Label
						subText={editing ? i18next.t("required-data") : undefined}
						isTitle
						text={
							id
								? editing
									? i18next.t("extraction-edit")
									: i18next.t("extraction-details")
								: i18next.t("extraction-create")
						}
					/>
					{editing && (
						<div
							className="text-danger"
							data-testid="delete-button"
							style={{ cursor: "pointer" }}
							onClick={() => setDeleteModal(true)}
						>
							{i18next.t("delete")}
						</div>
					)}
				</div>

				{/* Identificador */}
				<BasicInput
					label={i18next.t("extraction-identifier")}
					value={extraction.code}
					onChange={(e) => handleChangeExtraction({ ...extraction, code: e.target.value })}
					required={true}
					datatestid="code-input"
					className="mt-3"
					error={errors.code}
					showValue={showValue}
				/>

				{/* Location */}
				<AddLocationButtonsGroup
					creatingLocation={false}
					setSelectingLocation={setSelectingLocation}
					setSelectLocationMode={setSelectLocationMode}
					selectLocationMode={selectLocationMode}
					location={location}
					setLocation={setLocation}
					errors={errors}
					showValue={showValue}
				/>

				{/* Extraccion type */}
				<FieldTitle label={i18next.t("extraction-type")} className="mb-2" />
				{showValue ? (
					<div>{i18next.t(extraction.type_extraction)}</div>
				) : (
					<Stack direction="row" spacing={1}>
						{constants.EXTRACTION_TYPES.map((type_extraction) => (
							<Chip
								data-testid={`chip-${type_extraction}`}
								key={type_extraction}
								label={i18next.t(type_extraction)}
								variant={extraction.type_extraction === type_extraction ? "outlined" : "filled"}
								onClick={() => handleChangeExtraction({ ...extraction, type_extraction })}
								className={`rounded-3 ${
									extraction.type_extraction === type_extraction
										? "border-info text-primary"
										: "bg-white border"
								}
								${errors.type_extraction ? "border-danger" : ""}
							`}
							/>
						))}
					</Stack>
				)}
				{errors.type_extraction && <ErrorMessage error={errors.type_extraction} />}

				<BasicInput
					label={i18next.t("reference")}
					value={extraction.additional_info}
					onChange={(e) => handleChangeExtraction({ ...extraction, additional_info: e.target.value })}
					datatestid="additional-info-input"
					className="mt-3"
					showValue={showValue}
				/>

				{/* Actions */}
				<div className="d-flex mt-4 justify-content-end">
					<Button label={i18next.t(id ? "go-back" : "cancel")} onClick={onClose} className="ms-4" secondary />
					{id ? (
						editing ? (
							<Button
								label={i18next.t("save")}
								datatestid="save-button"
								onClick={handleCreateEditExtraction}
								className="ms-4"
							/>
						) : (
							<Button
								label={i18next.t("entity-edit", { entity: i18next.t("Extraction") })}
								datatestid="edit-button"
								onClick={() => setEditing(true)}
								className="ms-4"
								visible={utils.isAdminOrCollaborator()}
							/>
						)
					) : (
						<Button
							label={i18next.t("entity-create", { entity: i18next.t("Extraction") })}
							datatestid="create-button"
							onClick={handleCreateEditExtraction}
							className="ms-4"
						/>
					)}
				</div>
			</SimpleDialog>
		</>
	);
}
